import React, { useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { pesquisaAPI } from '../../lib/api';
import { Campo, Opcoes, Moeda, UFS, numeroDeMascara } from './PesquisaUI';

const INITIAL = {
  cidade: '', uf: 'RR', unidade: '', vinculo: '', horas: '12', horasOutra: '',
  valor: '', regime: '', producao: '', vale_para: '', valorFeriado: '',
  valorApendicectomia: '', valorAcessoVenoso: '',
  nome: '', contato: '', aceita: false, website: '',
};

const inputCls = 'rounded-[10px] border border-slate-300 px-3 py-2.5 text-[15px] focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary';

const erroLegivel = (err) => {
  const d = err?.response?.data?.detail;
  if (Array.isArray(d)) return d.map((x) => x.msg?.replace('Value error, ', '')).join(' · ');
  return d || 'Não consegui enviar agora. Tente de novo em instantes.';
};

export const PesquisaForm = ({ onEnviado }) => {
  const [f, setF] = useState(INITIAL);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState('');
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const horasNum = f.horas === 'outra' ? parseInt(f.horasOutra || '0', 10) : parseInt(f.horas, 10);
  const valorNum = numeroDeMascara(f.valor);

  const faltando = useMemo(() => {
    const x = [];
    if (!f.cidade.trim()) x.push('cidade');
    if (!f.unidade) x.push('tipo de unidade');
    if (!f.vinculo) x.push('vínculo');
    if (!horasNum) x.push('duração');
    if (!valorNum) x.push('valor');
    if (!f.regime) x.push('regime');
    if (!f.producao) x.push('produção');
    if (!f.vale_para) x.push('dia');
    return x;
  }, [f, horasNum, valorNum]);

  const enviar = async () => {
    if (faltando.length) return;
    setEnviando(true);
    setErro('');
    try {
      const res = await pesquisaAPI.criar({
        cidade: f.cidade.trim(), uf: f.uf, unidade: f.unidade, vinculo: f.vinculo,
        horas: horasNum, valor: valorNum, regime: f.regime, producao: f.producao,
        vale_para: f.vale_para,
        valor_feriado: f.vale_para === 'Dia de semana' ? numeroDeMascara(f.valorFeriado) || null : null,
        valor_apendicectomia: f.producao === 'Sim' ? numeroDeMascara(f.valorApendicectomia) || null : null,
        valor_acesso_venoso: f.producao === 'Sim' ? numeroDeMascara(f.valorAcessoVenoso) || null : null,
        nome: f.nome.trim() || null, contato: f.contato.trim() || null,
        aceita_contato: !!f.aceita, website: f.website,
      });
      onEnviado({ resumo: res.data.resumo, valorNum, horasNum });
    } catch (err) {
      setErro(erroLegivel(err));
    } finally {
      setEnviando(false);
    }
  };

  return (
    <section className="rounded-2xl bg-white p-5 sm:p-7 shadow-sm border border-slate-100" data-testid="pesquisa-form">
      <Campo titulo="Onde é o plantão" dica="Cidade e estado da unidade" testId="campo-local">
        <div className="flex gap-2.5">
          <input value={f.cidade} onChange={(e) => set('cidade', e.target.value)} placeholder="Cidade" className={`flex-1 min-w-0 ${inputCls}`} data-testid="pesquisa-cidade-input" />
          <select value={f.uf} onChange={(e) => set('uf', e.target.value)} className={`flex-none w-24 ${inputCls}`} data-testid="pesquisa-uf-select">
            {UFS.map((u) => <option key={u}>{u}</option>)}
          </select>
        </div>
      </Campo>

      <Campo titulo="Tipo de unidade" testId="campo-unidade">
        <Opcoes valor={f.unidade} onChange={(v) => set('unidade', v)} opcoes={['Hospital público', 'Hospital privado']} testPrefix="pesquisa-unidade" />
      </Campo>

      <Campo titulo="Vínculo" dica="PF é contracheque ou RPA. PJ é nota fiscal pela sua empresa." testId="campo-vinculo">
        <Opcoes valor={f.vinculo} onChange={(v) => set('vinculo', v)} opcoes={['PF', 'PJ']} testPrefix="pesquisa-vinculo" />
      </Campo>

      <Campo titulo="Duração do plantão" testId="campo-horas">
        <Opcoes
          valor={f.horas}
          onChange={(v) => set('horas', v)}
          opcoes={[{ v: '6', label: '6h' }, { v: '12', label: '12h' }, { v: '24', label: '24h' }, { v: 'outra', label: 'Outra' }]}
          testPrefix="pesquisa-horas"
        />
        {f.horas === 'outra' && (
          <div className="mt-3.5 rounded-[10px] p-3.5 bg-secondary-50 border border-secondary-100">
            <label className="block text-[14.5px] font-semibold text-secondary-700">Quantas horas?</label>
            <input
              inputMode="numeric" maxLength={2} value={f.horasOutra}
              onChange={(e) => set('horasOutra', e.target.value.replace(/\D/g, ''))}
              placeholder="Ex.: 8" className={`mt-2 w-full ${inputCls}`} data-testid="pesquisa-horas-outra-input"
            />
          </div>
        )}
      </Campo>

      <Campo titulo="Valor recebido por esse plantão" dica="Valor bruto, antes de descontos" testId="campo-valor">
        <Moeda valor={f.valor} onChange={(v) => set('valor', v)} testId="pesquisa-valor-input" />
      </Campo>

      <Campo titulo="Regime" dica="Fica na unidade o plantão inteiro ou fica disponível para ser chamado" testId="campo-regime">
        <Opcoes valor={f.regime} onChange={(v) => set('regime', v)} opcoes={['Presencial', 'Sobreaviso']} testPrefix="pesquisa-regime" />
      </Campo>

      <Campo titulo="Além do plantão, paga produção?" dica="Valor extra por cirurgia, procedimento ou atendimento" testId="campo-producao">
        <Opcoes valor={f.producao} onChange={(v) => set('producao', v)} opcoes={['Sim', 'Não']} testPrefix="pesquisa-producao" />
        {f.producao === 'Sim' && (
          <div className="mt-3.5 rounded-[10px] p-3.5 bg-secondary-50 border border-secondary-100" data-testid="pesquisa-procedimentos-box">
            <label className="block text-[14.5px] font-semibold text-secondary-700">Quanto recebe por procedimento? <span className="font-normal text-slate-500">(opcional)</span></label>
            <span className="block text-[13px] text-slate-500 mt-0.5 mb-3">Dois procedimentos de referência. Se não souber, pode pular.</span>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <span className="block text-[13px] font-medium text-slate-700 mb-1.5">Apendicectomia</span>
                <Moeda valor={f.valorApendicectomia} onChange={(v) => set('valorApendicectomia', v)} testId="pesquisa-apendicectomia-input" />
              </div>
              <div>
                <span className="block text-[13px] font-medium text-slate-700 mb-1.5">Acesso venoso central</span>
                <Moeda valor={f.valorAcessoVenoso} onChange={(v) => set('valorAcessoVenoso', v)} testId="pesquisa-acesso-venoso-input" />
              </div>
            </div>
          </div>
        )}
      </Campo>

      <Campo titulo="O valor que você informou vale para" testId="campo-vale-para">
        <Opcoes valor={f.vale_para} onChange={(v) => set('vale_para', v)} opcoes={['Dia de semana', 'Feriado', 'Indiferente']} testPrefix="pesquisa-vale" />
        {f.vale_para === 'Dia de semana' && (
          <div className="mt-3.5 rounded-[10px] p-3.5 bg-secondary-50 border border-secondary-100">
            <label className="block text-[14.5px] font-semibold text-secondary-700">E no feriado, quanto é o plantão?</label>
            <span className="block text-[13px] text-slate-500 mt-0.5 mb-2.5">Se não muda ou você não sabe, pode deixar em branco</span>
            <Moeda valor={f.valorFeriado} onChange={(v) => set('valorFeriado', v)} testId="pesquisa-valor-feriado-input" />
          </div>
        )}
      </Campo>

      <Campo titulo="Quer receber o resultado consolidado?" dica="Opcional e anônimo por padrão. Só usamos para mandar o resumo da pesquisa e falar com você se pedir." testId="campo-contato">
        <div className="space-y-2.5">
          <input value={f.nome} onChange={(e) => set('nome', e.target.value)} placeholder="Nome (opcional)" className={`w-full ${inputCls}`} data-testid="pesquisa-nome-input" />
          <input value={f.contato} onChange={(e) => set('contato', e.target.value)} placeholder="WhatsApp ou e-mail (opcional)" className={`w-full ${inputCls}`} data-testid="pesquisa-contato-input" />
          <label className="flex items-start gap-2.5 text-[13.5px] text-slate-600 cursor-pointer">
            <input type="checkbox" checked={f.aceita} onChange={(e) => set('aceita', e.target.checked)} className="mt-1 accent-[#1e4e7e]" data-testid="pesquisa-aceita-checkbox" />
            <span>Aceito que a Apex Medical Hub entre em contato sobre a minha situação tributária.</span>
          </label>
        </div>
        <input
          tabIndex={-1} autoComplete="off" value={f.website} onChange={(e) => set('website', e.target.value)}
          style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0 }} aria-hidden="true"
        />
      </Campo>

      <div className="mt-6">
        <button
          type="button" onClick={enviar} disabled={faltando.length > 0 || enviando}
          data-testid="pesquisa-enviar-button"
          className="w-full inline-flex items-center justify-center gap-2 rounded-[10px] bg-primary hover:bg-primary-800 px-4 py-3.5 font-semibold text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {enviando && <Loader2 className="w-4 h-4 animate-spin" />}
          {enviando ? 'Enviando...' : 'Enviar e ver o comparativo'}
        </button>
        <p className="mt-3 min-h-[21px] text-[14px] text-slate-500" data-testid="pesquisa-status-text">
          {erro ? <span className="font-medium text-red-600">{erro}</span>
            : faltando.length ? `Falta preencher: ${faltando.join(', ')}.`
            : <span className="font-medium text-secondary">Pronto para enviar.</span>}
        </p>
      </div>
    </section>
  );
};
